import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import classNames from 'classnames'
import { runInAction } from 'mobx'
import { observer } from 'mobx-react-lite'

import { useObservable } from '@campai/lib-web/components'

import { Button } from './Button'
import { CreateOrganizationInput, StepId } from './types'
import { createWelcome } from './01_Welcome'
import { createOrganization } from './02_Organization'
import { createAccounting } from './04_Accounting'
import { createFinish } from './06_Finish'
import createOrganizationBackground from './createOrganizationBackground.png'
import { newOrganizationAvailableCountries } from './utils'

export const CreateOrganization = observer(() => {
    const rootRef = useRef<HTMLDivElement | null>(null)
    const stageRef = useRef<HTMLDivElement | null>(null)
    const itemRefs = useRef<Array<HTMLDivElement | null>>([])
    const previousInputSignatureRef = useRef('')
    const layoutFrameRef = useRef<number | null>(null)
    const [activeIndex, setActiveIndex] = useState(0)
    const [layoutVersion, setLayoutVersion] = useState(0)
    const [stopTransition, setStopTransition] = useState(true)

    const scheduleLayoutUpdate = useCallback(() => {
        if (layoutFrameRef.current !== null) {
            return
        }

        layoutFrameRef.current = window.requestAnimationFrame(() => {
            layoutFrameRef.current = null
            setLayoutVersion((current) => current + 1)
        })
    }, [])

    const layout = useMemo(() => {
        const activeNode = itemRefs.current[activeIndex]
        const stageNode = stageRef.current

        if (!activeNode || !rootRef.current || !stageNode) {
            return {
                stageStyle: undefined,
                trackerStyle: undefined,
            }
        }

        if (rootRef.current.clientWidth < 1024) {
            return {
                stageStyle: undefined,
                trackerStyle: undefined,
            }
        }

        const stageRect = stageNode.getBoundingClientRect()
        const activeRect = activeNode.getBoundingClientRect()
        const stageWidth = stageNode.clientWidth
        const topWithinStage = activeRect.top - stageRect.top
        const translateY = rootRef.current.clientHeight / 2 - (topWithinStage + activeNode.clientHeight / 2)

        return {
            stageStyle: {
                transform: `translate(-50%, ${translateY}px)`,
            },
            trackerStyle: {
                top: `${topWithinStage + translateY}px`,
                left: `${(rootRef.current.clientWidth - stageWidth) / 2}px`,
                height: `${activeNode.clientHeight}px`,
                width: `${stageWidth}px`,
            },
        }
    }, [activeIndex, layoutVersion])

    useEffect(() => {
        const frame = window.requestAnimationFrame(() => {
            setLayoutVersion((current) => current + 1)

            window.requestAnimationFrame(() => {
                setStopTransition(false)
            })
        })

        return () => {
            window.cancelAnimationFrame(frame)
        }
    }, [])

    useEffect(() => {
        const rootNode = rootRef.current

        if (!rootNode) {
            return
        }

        const animation = rootNode.animate(
            [{ backgroundPosition: '36% 0%' }, { backgroundPosition: '65% 100%' }, { backgroundPosition: '36% 0%' }],
            {
                duration: 20000,
                easing: 'ease',
                iterations: Infinity,
            }
        )

        return () => {
            animation.cancel()
        }
    }, [])

    const input = useObservable<CreateOrganizationInput>(() => ({
        organization: {
            name: '',
            website: '',
            memberCount: 0,
            address: {
                country: newOrganizationAvailableCountries[0],
                zip: '',
                city: '',
                addressLine: '',
            },
            classification: {
                type: '',
                area: '',
                sports: [],
            },
        },
        finances: {
            accountingPlanType: null,
            fiscalMonthStart: 1,
            vatDueMethod: 'payment',
            accountingMode: 'none',
        },
        ui: {
            accountingModeSelected: false,
            noWebsite: false,
            finishState: 'idle',
            validationErrors: null,
        },
    }))

    const steps = useMemo(
        () => [createWelcome(input), createOrganization(input), createAccounting(input), createFinish(input)],
        [input]
    )
    const visibleSteps = steps.filter((step) => step.isAvailable)
    const activeStep = visibleSteps[activeIndex]!
    const disableBack = activeIndex === 0 || input.ui.finishState !== 'idle'
    const inputSignature = JSON.stringify({
        organization: input.organization,
        finances: input.finances,
        ui: {
            accountingModeSelected: input.ui.accountingModeSelected,
            noWebsite: input.ui.noWebsite,
        },
    })

    const handleNext = useCallback(() => {
        setActiveIndex((current) => (current < visibleSteps.length - 1 ? current + 1 : current))
    }, [visibleSteps.length])

    useEffect(() => {
        if (rootRef.current && rootRef.current.clientWidth < 1024) {
            rootRef.current.scrollTop = 0
        }
    }, [activeIndex])

    useLayoutEffect(() => {
        const rootNode = rootRef.current
        const stageNode = stageRef.current
        const activeNode = itemRefs.current[activeIndex]

        const resizeObserver =
            typeof ResizeObserver === 'undefined'
                ? null
                : new ResizeObserver(() => {
                      scheduleLayoutUpdate()
                  })

        if (resizeObserver) {
            if (rootNode) {
                resizeObserver.observe(rootNode)
            }

            if (stageNode) {
                resizeObserver.observe(stageNode)
            }

            if (activeNode) {
                resizeObserver.observe(activeNode)
            }
        }

        window.addEventListener('resize', scheduleLayoutUpdate)
        scheduleLayoutUpdate()

        return () => {
            resizeObserver?.disconnect()
            window.removeEventListener('resize', scheduleLayoutUpdate)

            if (layoutFrameRef.current !== null) {
                window.cancelAnimationFrame(layoutFrameRef.current)
                layoutFrameRef.current = null
            }
        }
    }, [activeIndex, scheduleLayoutUpdate, visibleSteps.length])

    useEffect(() => {
        if (
            previousInputSignatureRef.current &&
            previousInputSignatureRef.current !== inputSignature &&
            input.ui.validationErrors
        ) {
            runInAction(() => {
                input.ui.validationErrors = null
            })
        }

        previousInputSignatureRef.current = inputSignature
    }, [input.ui, input.ui.validationErrors, inputSignature])

    useEffect(() => {
        const errorPath = Object.keys(input.ui.validationErrors?.errors ?? {})[0]

        if (!errorPath) {
            return
        }

        const getStepIdForErrorPath = (path: string): StepId => {
            if (path === 'organization.name') {
                return 'welcome'
            }

            if (path.startsWith('organization.address.') || path.startsWith('organization.classification.')) {
                return 'organization'
            }

            if (path.startsWith('finances.')) {
                return 'accounting'
            }

            if (path === 'organization.website' || path === 'organization.memberCount') {
                return 'finish'
            }

            return 'welcome'
        }

        const errorStepIndex = visibleSteps.findIndex((step) => step.id === getStepIdForErrorPath(errorPath))

        if (errorStepIndex >= 0 && errorStepIndex !== activeIndex) {
            setActiveIndex(errorStepIndex)
        }
    }, [activeIndex, input.ui.validationErrors, visibleSteps])

    useEffect(() => {
        // Enforce light mode for the create organization flow since the UI is not designed for dark mode
        const classList = document.documentElement.classList
        const addedNoColorScheme = !classList.contains('no-color-scheme')

        classList.add('no-color-scheme')
        classList.remove('dark')

        return () => {
            if (addedNoColorScheme) {
                classList.remove('no-color-scheme')
            }

            const updateColorScheme = Reflect.get(window, 'updateColorScheme')
            if (typeof updateColorScheme === 'function') {
                updateColorScheme()
            }
        }
    }, [])

    return (
        <div
            ref={rootRef}
            className="relative isolate mx-auto h-dvh overflow-y-auto [-moz-osx-font-smoothing:grayscale] [-webkit-font-smoothing:antialiased] [background-image:linear-gradient(327deg,#1869b7,#8e50d5,#3d888c,#3b85b7,#8458a7,#31836e)] [background-position:36%_0%] [background-size:1200%_1200%] [text-rendering:optimizeLegibility] lg:h-screen lg:overflow-hidden lg:px-20"
        >
            <div
                aria-hidden="true"
                className="pointer-events-none absolute inset-0 bg-cover bg-center bg-no-repeat opacity-[0.58]"
                style={{ backgroundImage: `url("${createOrganizationBackground}")` }}
            />
            <div
                ref={stageRef}
                className={`relative z-[2] flex w-full flex-col items-center lg:absolute lg:left-1/2 lg:top-0 lg:w-[700px] ${
                    stopTransition ? 'transition-none' : 'transition-transform duration-[320ms] ease-in-out'
                } lg:[will-change:transform]`}
                style={layout.stageStyle}
            >
                {visibleSteps.map((step, index) => {
                    const isActive = index === activeIndex

                    return (
                        <div
                            key={index}
                            ref={(node) => {
                                itemRefs.current[index] = node
                            }}
                            className={classNames(
                                'relative z-[1] my-4 w-full rounded-lg border-2 border-transparent bg-white/80 p-5 transition-all duration-300 lg:bg-transparent lg:p-12',
                                { 'z-[2] opacity-100': isActive, 'hidden opacity-10 lg:block': !isActive }
                            )}
                        >
                            <step.Element
                                input={input}
                                onNext={step.isValid && activeIndex < visibleSteps.length - 1 ? handleNext : undefined}
                            />
                        </div>
                    )
                })}
            </div>

            <div
                className={`pointer-events-none relative z-[1] mx-4 mb-4 flex justify-center lg:absolute lg:left-0 lg:top-0 lg:mx-0 lg:mb-0 lg:block lg:max-w-full ${
                    stopTransition ? 'transition-none' : 'transition-all duration-[320ms] ease-in-out'
                }`}
                style={layout.trackerStyle}
            >
                <div className="absolute left-0 top-0 hidden h-full w-full rounded-[20px] bg-[linear-gradient(180deg,rgba(255,255,255,0.65)_0%,rgba(255,255,255,0.35)_100%)] shadow-[inset_0_0.5px_0_1px_rgba(255,255,255,0.23),inset_0_1px_0_0_rgba(255,255,255,0.66),0_4px_16px_rgba(0,0,0,0.12)] backdrop-blur-[30px] lg:block" />
                <div className="pointer-events-auto relative z-[3] flex gap-3 lg:absolute lg:left-[calc(100%+16px)] lg:top-1/2 lg:block lg:-translate-y-1/2">
                    <Button
                        disabled={disableBack}
                        onClick={() => setActiveIndex((current) => (current > 0 ? current - 1 : current))}
                        rounded="full"
                        icon={
                            <svg
                                width="18"
                                height="18"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                                xmlns="http://www.w3.org/2000/svg"
                            >
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 15l7-7 7 7" />
                            </svg>
                        }
                    />
                    <Button
                        className="lg:mt-2.5"
                        disabled={activeIndex === visibleSteps.length - 1 || !activeStep.isValid}
                        onClick={handleNext}
                        rounded="full"
                        icon={
                            <svg
                                width="18"
                                height="18"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                                xmlns="http://www.w3.org/2000/svg"
                            >
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
                            </svg>
                        }
                    />
                </div>
            </div>
        </div>
    )
})
